/**
 * Утилита видимости и извлечения ошибок полей TanStack Form.
 * Ошибка берётся из меты поля (field.state.meta), а не из стора формы — актуальное состояние в текущем рендере.
 *
 * Использование:
 *   error={
 *     shouldShowFieldError(field.state.meta, tanstackForm.state)
 *       ? getFieldErrorFromMeta(field.state.meta)
 *       : undefined
 *   }
 * Видимость: (touch|blur) && !isPristine, либо submissionAttempts > 0.
 */

/**
 * Извлекает текст из ошибки TanStack Form.
 * Ошибка может быть строкой, объектом StandardSchemaV1Issue { message } или массивом таких объектов.
 */
function extractErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') return error || undefined;
  if (typeof error === 'object' && error !== null && 'message' in error)
    return (error as { message?: string }).message || undefined;
  if (Array.isArray(error)) {
    const first = error[0];
    return extractErrorMessage(first);
  }
  return undefined;
}

/**
 * Возвращает первую ошибку поля TanStack Form по приоритету событий.
 * По умолчанию: onChange → onBlur → onSubmit → onDynamic → onServer (серверные ошибки).
 * onDynamic — для revalidateLogic (валидация на submit и change после первой отправки).
 */
export const getFieldError = (
  errorMap: Record<string, unknown>,
  priority: string[] = ['onChange', 'onBlur', 'onSubmit', 'onDynamic', 'onServer']
): string | undefined =>
  priority.reduce<string | undefined>(
    (acc, key) => acc || extractErrorMessage(errorMap[key]),
    undefined
  );

/** Минимальный интерфейс меты поля (совместим с @tanstack/react-form). */
export interface FieldMetaVisibility {
  isTouched?: boolean;
  isBlurred?: boolean;
  /** false — значение отличалось от начального (не «чистое»). */
  isPristine?: boolean;
}

/** Минимальный интерфейс состояния формы для проверки попыток отправки. */
export interface FormStateVisibility {
  submissionAttempts?: number;
}

/** Мета поля с errorMap (TanStack Form). */
export interface FieldMetaWithErrorMap {
  errorMap?: Record<string, unknown>;
}

/**
 * Мета поля для `shouldShowFieldError`: touch/blur + errorMap.
 * Пересечение нужно, чтобы `{ errorMap?: ... }` из узких типов в формах был совместим с TS
 * (иначе слабый тип `FieldMetaVisibility` без общих ключей с `{ errorMap }` даёт ошибку).
 */
export type FieldMetaForVisibility = FieldMetaVisibility & FieldMetaWithErrorMap;

/**
 * Возвращает true, если ошибку поля нужно показывать в UI:
 * - после взаимодействия: (touched или blur) и значение менялось (!isPristine);
 * - или после попытки отправки формы (submissionAttempts > 0).
 */
export function shouldShowFieldError(
  fieldMeta: FieldMetaForVisibility | null | undefined,
  formState: FormStateVisibility | null | undefined
): boolean {
  const showAfterEdit =
    !!(fieldMeta?.isTouched || fieldMeta?.isBlurred) && fieldMeta?.isPristine === false;
  const showAfterSubmitAttempt =
    formState?.submissionAttempts != null && formState.submissionAttempts > 0;
  return !!(showAfterEdit || showAfterSubmitAttempt);
}

/**
 * Возвращает строку ошибки из меты поля (errorMap).
 * Использовать внутри form.Field, чтобы брать ошибку из field.state.meta, а не из стора по имени.
 */
export function getFieldErrorFromMeta(fieldMeta: FieldMetaWithErrorMap | null | undefined): string {
  return getFieldError(fieldMeta?.errorMap ?? {}) ?? '';
}
